import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ReCAPTCHA from 'react-google-recaptcha';
import Stack from '@mui/material/Stack';
import CloseIcon from '@mui/icons-material/Close';
import { alpha } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// third party
import * as Yup from 'yup';
import { Formik } from 'formik';

// project imports
import AnimateButton from 'ui-component/extended/AnimateButton';
import useAuth from 'hooks/useAuth';
import useScriptRef from 'hooks/useScriptRef';
import { strengthColor, strengthIndicator } from 'utils/password-strength';

// assets
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { StringColorProps } from 'types';
import SpinnerLoader from 'ui-component/Spinner';
import { styled, Theme } from '@mui/material/styles';

import { useNavigate } from 'react-router-dom';

interface RegisterNotice {
    theme: Theme;
}

interface RegisterView {
    theme: Theme;
}

const RegisterNotice = styled(Stack)(({ theme }: RegisterNotice) => ({
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: '0px',
    left: '0px',
    padding: '22px',
    zIndex: 10,
    background: '#000',
    '.MuiTypography-h5': {
        fontSize: '20px',
        color: '#fff',
        lineHeight: '36px',
        fontWeight: 600
    },
    '.MuiTypography-body1': {
        fontSize: '14px',
        lineHeight: '20px',
        marginBottom: '10px'
    }
}));

const RegisterView = styled(Stack)(({ theme }: RegisterView) => ({
    '.MuiFormControl-root': { marginBottom: '8px' },
    'p.Mui-error': {
        position: 'absolute',
        bottom: '-20px',
        left: '-14px',
        whiteSpace: 'nowrap',
        [theme.breakpoints.down(380)]: {
            fontSize: '10px'
        }
    },
    '.codeselect': {
        '.MuiInputBase-input': { padding: '15.5px 10px' }
    },
    '.MuiCheckbox-colorWarning': {
        color: '#ff0000',
        '.svg': { fill: '#ff0000' },
        '.h6': { color: '#ff0000' }
    }
}));

const countryCodes = [
    {
        label: 'Philippines',
        src: 'https://flagicons.lipis.dev/flags/4x3/ph.svg',
        value: '+63',
        country_code: '+63'
    }
];
type LoginType = 'email' | 'phone';
const phone = 'phone';

interface KYCFormValues {
    selfieWithID: File[];
    idUpload: File[];
}

const isFile = (value: unknown): value is File => {
    return value instanceof File;
};

const fileValidation = Yup.array()
    .min(1, 'File is required')
    .max(1, 'You can upload up to 4 images')
    .of(
        Yup.mixed()
            .test('fileSize', 'File size is too large (max 1MB)', (value) => {
                if (!value) return true;
                if (isFile(value)) {
                    return value.size <= 1024 * 1024; // 1MB
                }
                return false;
            })
            .test('fileType', 'Unsupported format', (value) => {
                if (!value) return true;
                if (isFile(value)) {
                    return ['image/jpeg', 'image/png'].includes(value.type);
                }
                return false;
            })
    );
// ===========================|| FIREBASE - REGISTER ||=========================== //

interface FormValues {
    fullName: string;
    email: string;
    dateOfBirth: Date | null;
    selfieWithID: File[];
    idUpload: File[];
}

interface PreviewFile {
    file: File;
    preview: string; // base64
}

const FileUploadPreview: React.FC<{
    label: string;
    name: keyof FormValues;
    value: File[];
    setFieldValue: (field: string, value: any) => void;
    error?: string;
    touched?: boolean;
}> = ({ label, name, value, setFieldValue, error, touched }) => {
    const theme = useTheme();
    const [previews, setPreviews] = useState<string[]>([]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files ? Array.from(event.target.files).slice(0, 1) : [];
        setFieldValue(name, files);

        // Convert to base64 previews
        Promise.all(
            files.map((file) => {
                return new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result as string);
                    reader.onerror = reject;
                    reader.readAsDataURL(file);
                });
            })
        ).then((base64Previews) => {
            setPreviews(base64Previews);
        });
    };

    return (
        <FormControl fullWidth margin="normal" error={touched && Boolean(error)}>
            <Box
                sx={{
                    color: `${theme.palette.secondary.main} !important`,
                    border: '2px dashed',
                    borderRadius: '4px',
                    padding: '10px 12px',
                    margin: '0px 0px 10px 0px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: 'transparent',
                    fontWeight: '800',
                    borderColor: theme.palette.secondary.main,
                    cursor: 'pointer',
                    '&:hover': {
                        borderColor: 'rgba(255, 255, 255, 0.5)'
                    }
                }}
                component="label"
            >
                <Typography>Upload {label}</Typography>
                <input type="file" hidden multiple accept="image/jpeg,image/png" onChange={handleFileChange} />
            </Box>

            {previews.length > 0 && (
                <Grid container spacing={2} mb={1}>
                    {previews.map((preview, index) => (
                        <Grid item xs={3} key={index}>
                            <img
                                src={preview}
                                alt={`preview-${index}`}
                                style={{
                                    width: '100%',
                                    borderRadius: 6,
                                    objectFit: 'cover',
                                    maxHeight: '100px'
                                }}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}

            {touched && error && <FormHelperText sx={{ mb: '13px' }}>{error}</FormHelperText>}
        </FormControl>
    );
};

const JWTRegister = ({
    loginMode,
    handleStepChange,
    getRegisterData,
    ...others
}: {
    loginMode: LoginType;
    handleStepChange: Function;
    getRegisterData: Function;
    [key: string]: any;
}) => {
    const theme = useTheme();
    const scriptedRef = useScriptRef();

    const [showPassword, setShowPassword] = React.useState(false);
    const [notice, setNotice] = React.useState(false);
    const [checked, setChecked] = React.useState(false);
    const [ageCheck, seAgeChecked] = React.useState(false);
    const [rulesChecked, seRulesChecked] = React.useState(false);
    const [isAgeError, setIsAgeError] = React.useState(false);
    const [isTerrmError, setIsTermError] = React.useState(false);
    const [isRulesError, setIsRulesError] = React.useState(false);

    const [strength, setStrength] = React.useState(0);
    const [level, setLevel] = React.useState<StringColorProps>();

    const recaptchaRef = useRef<ReCAPTCHA | null>(null);
    const { signupOtp } = useAuth();
    const navigate = useNavigate();

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleMouseDownPassword = (event: React.SyntheticEvent) => {
        event.preventDefault();
    };

    const changePassword = (value: string) => {
        const temp = strengthIndicator(value);
        setStrength(temp);
        setLevel(strengthColor(temp));
    };

    const handlePageChange = () => {
        navigate(`/termscondition`);
    };

    const handlePopup = () => {
        setNotice(true);
    };

    useEffect(() => {
        changePassword('123456');
        //handleStepChange('verification');
    }, []);

    let schema =
        loginMode === phone
            ? {
                  email: Yup.string().email('Must be a valid email').max(255),
                  phone: Yup.string()
                      .matches(/^\+?[0-9]*$/, 'Phone number can only contain numbers')
                      .min(10, 'Please enter 10 digits numbe')
                      .max(10, 'Phone number cannot exceed 10 digits')
                      .required('Mobile number is required'),
                  firstName: Yup.string().max(30).required('First Name is required'),
                  lastName: Yup.string().max(30).required('Last Name is required'),
                  password: Yup.string().max(255).min(6, 'Password must be at least 6 digits').required('Password is required'),
                  passwordConfirmation: Yup.string()
                      .oneOf([Yup.ref('password'), ''], 'Passwords must match')
                      .required('Confirm Password is required'),
                //   selfieWithID: fileValidation.required('Selfie with ID is required'),
                //   idUpload: fileValidation.required('ID Upload is required'),
                  dob: Yup.date()
                      .required('Date of Birth is required')
                      .max(new Date(new Date().setFullYear(new Date().getFullYear() - 21)), 'You must be at least 21 years old')
              }
            : {
                  email: Yup.string().email('Must be a valid email').max(255).required('Email is required'),
                  phone: Yup.string()
                      .matches(/^\+?[0-9]*$/, 'Phone number can only contain numbers')
                      .min(10, 'Please enter 10 digits numbe')
                      .max(10, 'Phone number cannot exceed 10 digits'),
                  firstName: Yup.string().max(30).required('First Name is required'),
                  lastName: Yup.string().max(30).required('Last Name is required'),
                  password: Yup.string().max(255).required('Password is required'),
                  passwordConfirmation: Yup.string()
                      .oneOf([Yup.ref('password'), ''], 'Passwords must match')
                      .required('Confirm Password is required'),
                //   selfieWithID: fileValidation.required('Selfie with ID is required'),
                //   idUpload: fileValidation.required('ID Upload is required'),
                  dob: Yup.date()
                      .required('Date of Birth is required')
                      .max(new Date(new Date().setFullYear(new Date().getFullYear() - 21)), 'You must be at least 21 years old')
              };

    return (
        <>
            {!notice && false && (
                <RegisterNotice>
                    <Box>
                        <Grid container>
                            <Grid item xs={10}>
                                <Typography variant="h5">IMPORTANT NOTICE</Typography>
                            </Grid>
                            <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    sx={{
                                        minWidth: '42px',
                                        width: '42px',
                                        background: alpha(theme.palette.secondary.main, 1),
                                        color: theme.palette.grey[500],
                                        '&:hover': {
                                            background: alpha(theme.palette.secondary.main, 1),
                                            color: theme.palette.grey[500]
                                        }
                                    }}
                                    onClick={handlePopup}
                                >
                                    <CloseIcon />
                                </Button>
                            </Grid>
                            {/* <Grid item xs={12}>
                                <Box sx={{ mt: 3, mb: 3 }}>
                                    <Typography variant="body1">
                                        The following personalities are not allowed to register and/or play in this online gaming website:
                                    </Typography>
                                    <Typography variant="body1">
                                        Government Official or employee connected directly with the operation of the Government or any of
                                        its agencies.
                                    </Typography>
                                    <Typography variant="body1">
                                        Member of the Armed Forces of the Philippines, including the Army, Navy, Air Force, or the
                                        Philippine National Police.
                                    </Typography>
                                    <Typography variant="body1">Persons under 21 years of age.</Typography>
                                    <Typography variant="body1">
                                        Persons included in the PAGCOR’s National Database of Restricted Persons (NDRP).
                                    </Typography>
                                    <Typography variant="body1">Gaming Employment License (GEL) holder.</Typography>
                                    <Typography variant="body1">
                                        Accounts that do not complete the full KYC process within the 72-hour period will be subject to
                                        permanent suspension and forfeiture of any funds in the account.
                                    </Typography>
                                    <Typography variant="body1">
                                        Funds or credits in the account of a player who is found ineligible to play shall mean forfeiture of
                                        said funds/credit in favor of the Government.
                                    </Typography>
                                    <Typography variant="body1">You are prohibited from playing in open and public places.</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 3 }}>
                                        <Button
                                            variant="contained"
                                            onClick={handlePopup}
                                            sx={{
                                                background: alpha(theme.palette.secondary.main, 1),
                                                color: theme.palette.grey[500],
                                                '&:hover': {
                                                    background: alpha(theme.palette.secondary.main, 1),
                                                    color: theme.palette.grey[500]
                                                }
                                            }}
                                        >
                                            Proceed to registration
                                        </Button>
                                    </Box>
                                </Box>
                            </Grid> */}

                            <Grid item xs={12}>
                                <Box sx={{ mt: 3, mb: 3 }}>
                                    <Typography variant="body1">
                                        The following personalities are not allowed to register and/or play in this online gaming website:
                                    </Typography>
                                    <Typography variant="body1">Persons under 21 years of age</Typography>
                                    <Typography variant="body1">
                                        Persons who are government officials, government employees, or members of the Armed Forces of the
                                        Philippines, including the Army, Navy, Air Force, and Philippine National Police
                                    </Typography>
                                    <Typography variant="body1">Persons who hold a Gaming Employment License (GEL)</Typography>
                                    <Typography variant="body1">
                                        Persons listed in the PAGCOR National Database of Restricted Persons (NDRP)
                                    </Typography>
                                    <Typography variant="body1">Playing in open and public places are prohibited.</Typography>
                                    <Typography variant="body1">
                                        For withdrawals via PESONet, the processing time is 3-5 banking days if the amount exceeds 50.000
                                        pesos Funds or credits in the account of player who is found ineligible to play shall mean
                                        forfeiture of said funds/credits in favor of the Government.
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 3 }}>
                                        <Button
                                            variant="contained"
                                            onClick={handlePopup}
                                            sx={{
                                                background: alpha(theme.palette.secondary.main, 1),
                                                color: theme.palette.grey[500],
                                                '&:hover': {
                                                    background: alpha(theme.palette.secondary.main, 1),
                                                    color: theme.palette.grey[500]
                                                }
                                            }}
                                        >
                                            Proceed to registration
                                        </Button>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </RegisterNotice>
            )}
            <RegisterView>
                <Formik
                    initialValues={{
                        email: '',
                        password: '000000',
                        passwordConfirmation: '000000',
                        firstName: '',
                        lastName: '',
                        phone: '',
                        submit: null,
                        countrycode: '+63',
                        token: '',
                        dob: 'null',
                        // selfieWithID: [],
                        // idUpload: []
                    }}
                    validationSchema={Yup.object().shape(schema)}
                    onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
                        // if (!ageCheck) {
                        //     setIsAgeError(true);
                        //     return;
                        // }
                        // if (!checked) {
                        //     setIsTermError(true);
                        //     return;
                        // }

                        // if (!rulesChecked) {
                        //     setIsRulesError(true);
                        //     return;
                        // }

                        try {
                            const response: any = await signupOtp(values.phone, values.email, values.countrycode, '', loginMode);
                            if (recaptchaRef.current) recaptchaRef.current.reset();
                            const result = response.body;
                            if (result?.token !== undefined && scriptedRef.current) {
                                handleStepChange('verification');
                                values.token = result.token;
                                // debugger;
                                getRegisterData(values);
                            } else {
                                setStatus({ success: false });
                                setErrors({ submit: result.error });
                                setSubmitting(false);
                            }
                        } catch (err: any) {
                            if (recaptchaRef.current) recaptchaRef.current.reset();
                            if (scriptedRef.current) {
                                setStatus({ success: false });
                                setErrors({ submit: err.response.data.message });
                                setSubmitting(false);
                            }
                        }
                    }}
                >
                    {({ errors, setFieldValue, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
                        <form noValidate onSubmit={handleSubmit} {...others}>
                            <Grid container spacing={{ xs: 0, sm: 2 }} sx={{}}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl
                                        fullWidth
                                        error={Boolean(touched.firstName && errors.firstName)}
                                        sx={{ ...theme.typography.customInput }}
                                    >
                                        <TextField
                                            fullWidth
                                            id="firstName"
                                            name="firstName"
                                            label="First Name"
                                            value={values.firstName}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.firstName && Boolean(errors.firstName)}
                                            helperText={touched.firstName && errors.firstName}
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl
                                        fullWidth
                                        error={Boolean(touched.lastName && errors.lastName)}
                                        sx={{ ...theme.typography.customInput }}
                                    >
                                        <TextField
                                            fullWidth
                                            id="outlined-adornment-email-register"
                                            type="text"
                                            value={values.lastName}
                                            label="Last Name"
                                            name="lastName"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            inputProps={{}}
                                            error={touched.lastName && Boolean(errors.lastName)}
                                            helperText={touched.lastName && errors.lastName}
                                        />
                                    </FormControl>
                                </Grid>
                            </Grid>

                            {loginMode === 'email' && (
                                <FormControl
                                    fullWidth
                                    error={Boolean(touched.email && errors.email)}
                                    sx={{ ...theme.typography.customInput }}
                                >
                                    <TextField
                                        id="outlined-adornment-email-register"
                                        type="email"
                                        value={values.email}
                                        name="email"
                                        label="Email Address"
                                        onBlur={handleBlur}
                                        onChange={handleChange}
                                        inputProps={{}}
                                        error={touched.email && Boolean(errors.email)}
                                        helperText={touched.email && errors.email}
                                    />
                                </FormControl>
                            )}
                            {loginMode === 'phone' && (
                                <>
                                    <Box display="flex" alignItems="center" gap={1} sx={{ alignItems: 'flex-start' }}>
                                        <FormControl
                                            fullWidth
                                            error={Boolean(touched.countrycode && errors.countrycode)}
                                            sx={{
                                                ...theme.typography.customInput,
                                                width: '30%',
                                                [theme.breakpoints.down(600)]: {
                                                    width: '30%'
                                                }
                                            }}
                                        >
                                            <InputLabel
                                                sx={{
                                                    position: 'absolute',
                                                    top: '0px !important'
                                                }}
                                                id="age-select"
                                            >
                                                Country Code
                                            </InputLabel>
                                            <Select
                                                labelId="outlined-adornment-countrycode-register"
                                                id="outlined-adornment-countrycode-register"
                                                value={values.countrycode}
                                                name="countrycode"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                label="Country Code"
                                                className="codeselect"
                                                sx={{ padding: '0px', fontSize: '12px', fontWeight: '700' }}
                                            >
                                                {countryCodes.map((country: any) => (
                                                    <MenuItem
                                                        className=""
                                                        sx={{
                                                            [theme.breakpoints.down(600)]: {
                                                                padding: '0px'
                                                            }
                                                        }}
                                                        key={country.country_code}
                                                        value={country.country_code}
                                                    >
                                                        <img
                                                            loading="lazy"
                                                            width={15}
                                                            height={15}
                                                            src={country.src}
                                                            alt={`Flag of ${country.label}`}
                                                            style={{
                                                                marginRight: 8,
                                                                marginTop: '3px',
                                                                [theme.breakpoints.down(600)]: {
                                                                    marginRight: 4
                                                                }
                                                            }}
                                                        />
                                                        <Typography
                                                            sx={{
                                                                fontSize: '14px',
                                                                fontWeight: '900',
                                                                [theme.breakpoints.down(600)]: {
                                                                    fontSize: '12px',
                                                                    fontWeight: '700'
                                                                }
                                                            }}
                                                        >
                                                            ({country.country_code})
                                                        </Typography>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl
                                            fullWidth
                                            error={Boolean(touched.phone && errors.phone)}
                                            sx={{
                                                ...theme.typography.customInput,
                                                width: '70%',
                                                [theme.breakpoints.down(600)]: {
                                                    width: '70%'
                                                }
                                            }}
                                        >
                                            <TextField
                                                id="outlined-adornment-email-register"
                                                type="tel"
                                                value={values.phone}
                                                name="phone"
                                                label="Mobile"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                inputProps={{
                                                    inputMode: 'numeric',
                                                    pattern: '[0-9]*',
                                                    maxLength: 10
                                                }}
                                                error={touched.phone && Boolean(errors.phone)}
                                                helperText={touched.phone && errors.phone}
                                            />
                                        </FormControl>
                                    </Box>
                                </>
                            )}
                            <Grid container spacing={{ xs: 0, sm: 2 }} sx={{}}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl
                                        fullWidth
                                        error={Boolean(touched.password && errors.password)}
                                        sx={{ ...theme.typography.customInput }}
                                    >
                                        <TextField
                                            id="outlined-adornment-password-register"
                                            type={showPassword ? 'text' : 'password'}
                                            value={values.password}
                                            name="password"
                                            label="Password"
                                            onBlur={handleBlur}
                                            error={touched.password && Boolean(errors.password)}
                                            helperText={touched.password && errors.password}
                                            onChange={(e) => {
                                                handleChange(e);
                                                changePassword(e.target.value);
                                            }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={handleClickShowPassword}
                                                            onMouseDown={handleMouseDownPassword}
                                                            edge="end"
                                                            size="large"
                                                        >
                                                            {showPassword ? <Visibility /> : <VisibilityOff />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </FormControl>

                                    {values.password && strength !== 0 && (
                                        <FormControl sx={{ mb: '0px !important' }} fullWidth>
                                            <Box sx={{ mb: 0 }}>
                                                <Grid container spacing={0} alignItems="center">
                                                    <Grid item>
                                                        <Box sx={{ width: 100, height: 8, borderRadius: '10px', bgcolor: level?.color }} />
                                                    </Grid>
                                                    <Grid item>
                                                        <Typography
                                                            variant="subtitle1"
                                                            fontSize="0.75rem"
                                                            sx={{ pl: '8px', fontWeight: '900' }}
                                                        >
                                                            {level?.label}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </FormControl>
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl
                                        className="33333"
                                        fullWidth
                                        error={Boolean(touched.passwordConfirmation && errors.passwordConfirmation)}
                                        sx={{
                                            ...theme.typography.customInput,
                                            [theme.breakpoints.down(600)]: {
                                                paddingTop: '0px'
                                            }
                                        }}
                                    >
                                        <TextField
                                            id="outlined-adornment-password-register"
                                            type={showPassword ? 'text' : 'password'}
                                            value={values.passwordConfirmation}
                                            name="passwordConfirmation"
                                            label="Confirm Password"
                                            onBlur={handleBlur}
                                            error={touched.passwordConfirmation && Boolean(errors.passwordConfirmation)}
                                            helperText={touched.passwordConfirmation && errors.passwordConfirmation}
                                            onChange={(e) => {
                                                handleChange(e);
                                            }}
                                            inputProps={{}}
                                        />
                                    </FormControl>
                                </Grid>
                            </Grid>
                            <Grid container spacing={{ xs: 0, sm: 2 }} sx={{}}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl
                                        fullWidth
                                        error={Boolean(touched.dob && errors.dob)}
                                        sx={{ ...theme.typography.customInput }}
                                    >
                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                            <DatePicker
                                                label="Date of Birth"
                                                value={values.dob}
                                                onChange={(value) => {
                                                    handleChange({
                                                        target: { name: 'dob', value }
                                                    });
                                                }}
                                                slotProps={{
                                                    textField: {
                                                        variant: 'outlined',
                                                        fullWidth: true,
                                                        error: Boolean(touched.dob && errors.dob),
                                                        helperText: touched.dob && errors.dob,
                                                        onBlur: handleBlur
                                                    }
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </FormControl>
                                </Grid>
                            </Grid>
                            {/* upload files */}
                            {/* <Grid container spacing={{ xs: 0, sm: 2 }} sx={{}}>
                                <Grid item xs={12} sm={6}>
                                    <FileUploadPreview
                                        label="Selfie Holding ID"
                                        name="selfieWithID"
                                        value={values.selfieWithID}
                                        setFieldValue={setFieldValue}
                                        error={errors.selfieWithID as string}
                                        touched={!!touched.selfieWithID}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FileUploadPreview
                                        label="ID Upload"
                                        name="idUpload"
                                        value={values.idUpload}
                                        setFieldValue={setFieldValue}
                                        error={errors.idUpload as string}
                                        touched={!!touched.idUpload}
                                    />
                                </Grid>
                            </Grid> */}
                            {/* <Grid container alignItems="center" justifyContent="space-between">
                                <Grid item>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                // checked={checked}
                                                onChange={(event) => seAgeChecked(event.target.checked)}
                                                name="checked"
                                                color={`${isAgeError ? 'warning' : 'primary'}`}
                                                sx={{ padding: '5px 9px' }}
                                            />
                                        }
                                        label={
                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    color: theme.palette.secondary.main,
                                                    whiteSpace: 'nowrap',
                                                    [theme.breakpoints.down(600)]: {
                                                        fontSize: '12px'
                                                    }
                                                }}
                                            >
                                                I declare that I am at least 21 years old
                                            </Typography>
                                        }
                                    />
                                </Grid>
                            </Grid>
                            <Grid container alignItems="center" justifyContent="space-between">
                                <Grid item>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                onChange={(event) => setChecked(event.target.checked)}
                                                name="checked"
                                                color={`${isTerrmError ? 'warning' : 'primary'}`}
                                                sx={{ padding: '5px 9px' }}
                                            />
                                        }
                                        label={
                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    color: theme.palette.secondary.main,
                                                    [theme.breakpoints.down(600)]: {
                                                        fontSize: '12px'
                                                    }
                                                }}
                                            >
                                                Agree with &nbsp;
                                                <Typography
                                                    onClick={() => handlePageChange()}
                                                    variant="subtitle1"
                                                    component={Link}
                                                    to="/termscondition"
                                                    sx={{ color: theme.palette.secondary.main }}
                                                >
                                                    Terms & Condition.
                                                </Typography>
                                            </Typography>
                                        }
                                    />
                                </Grid>
                            </Grid>
                            <Grid container alignItems="center" justifyContent="space-between">
                                <Grid item>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                onChange={(event) => seRulesChecked(event.target.checked)}
                                                name="checked"
                                                color={`${isRulesError ? 'warning' : 'primary'}`}
                                                sx={{ padding: '5px 9px' }}
                                            />
                                        }
                                        label={
                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    color: theme.palette.secondary.main,
                                                    whiteSpace: 'nowrap',
                                                    [theme.breakpoints.down(600)]: {
                                                        fontSize: '12px'
                                                    }
                                                }}
                                            >
                                                I am not a government official, or employee connected <br /> directly with the operation of
                                                the Government or any <br /> of its agencies, member of the Armed Forces of the <br />
                                                Philippines, including the Army, Navy, Air Force, <br /> or the Philippine National Police.
                                                <br />I am not a Gaming Employment License (GEL) holder.
                                            </Typography>
                                        }
                                    />
                                </Grid>
                            </Grid> */}
                            <Box sx={{ mt: 2 }}>
                                <AnimateButton>
                                    <Button
                                        sx={{
                                            backgroundColor: theme.palette.secondary.main,
                                            color: theme.palette.grey[500],
                                            boxShadow: 'inherit',
                                            mb: '10px',
                                            fontSize: '16px',
                                            fontWeight: '700',
                                            '&:hover': {
                                                backgroundColor: theme.palette.secondary.main,
                                                color: theme.palette.grey[500],
                                                boxShadow: 'inherit'
                                            }
                                        }}
                                        color="secondary"
                                        disabled={isSubmitting}
                                        fullWidth
                                        size="large"
                                        type="submit"
                                        variant="contained"
                                    >
                                        Sign up{isSubmitting && <SpinnerLoader />}
                                    </Button>
                                </AnimateButton>
                            </Box>
                            {errors.submit && (
                                <Box sx={{ mt: 1, position: 'relative' }}>
                                    <FormHelperText
                                        sx={{
                                            fontSize: '.85rem',
                                            fontWeight: '600',
                                            textAlign: 'center',
                                            position: 'inherit !important',
                                            bottom: '0px !important',
                                            left: '0px !important'
                                        }}
                                        error
                                    >
                                        {errors.submit}
                                    </FormHelperText>
                                </Box>
                            )}
                        </form>
                    )}
                </Formik>
            </RegisterView>
        </>
    );
};

export default JWTRegister;
